import React, { useMemo } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { Flex, FormControl, FormErrorMessage, FormLabel, Input } from '@chakra-ui/react';

import TooltipHelp from '../../../../common/components/TooltipHelp';
import { CountryResponse } from '../../../../common/models/country';
import { useTranslations } from '../../../../contexts/LocalizationContext';
import { CitySelect } from '../../../Restaurants/components/CitySelect/CitySelect';
import { CountrySelect } from '../../../Restaurants/components/CountrySelect/CountrySelect';

export const QuickContactUserAddress = () => {
  const translations = useTranslations();
  const {
    control,
    formState: { errors },
    setValue,
  } = useFormContext();

  const trimValue =
    (fieldName: 'street' | 'streetNumber' | 'postalCode') => (event: React.FocusEvent<HTMLInputElement>) =>
      setValue(fieldName, event.target.value.trim());

  const watchCountry = useWatch({ control: control, name: 'country' });
  const selectedCountry = useMemo(() => watchCountry, [watchCountry]);
  const isCityDisabled = useMemo(() => !selectedCountry, [selectedCountry]);

  const handleCountryChange = (onChange: (e: unknown) => void) => {
    return (country: CountryResponse | null) => {
      onChange(country);
      setValue('city', null);
    };
  };

  return (
    <>
      <Flex>
        <Controller
          control={control}
          render={({ field: { value, onChange } }) => {
            return (
              <FormControl isInvalid={!!errors.street}>
                <FormLabel htmlFor={'street'}>{translations('quick_contacts_add_new_contact_street_name')}</FormLabel>
                <Input
                  value={value ?? ''}
                  onChange={onChange}
                  id={'street'}
                  isInvalid={!!errors.street}
                  autoComplete="off"
                  variant="transparentUnderline"
                  onBlur={trimValue('street')}
                />
                <FormErrorMessage>{errors.street?.message?.toString()}</FormErrorMessage>
              </FormControl>
            );
          }}
          name={'street'}
        />
      </Flex>
      <Flex w={'100%'} gap={4}>
        <Controller
          control={control}
          render={({ field: { value, onChange } }) => {
            return (
              <FormControl isInvalid={!!errors.streetNumber}>
                <FormLabel htmlFor={'streetNumber'}>
                  {translations('quick_contacts_add_new_contact_street_number')}
                </FormLabel>
                <Input
                  value={value ?? ''}
                  onChange={onChange}
                  id={'streetNumber'}
                  isInvalid={!!errors.streetNumber}
                  autoComplete="off"
                  variant="transparentUnderline"
                  onBlur={trimValue('streetNumber')}
                />
                <FormErrorMessage>{errors.streetNumber?.message?.toString()}</FormErrorMessage>
              </FormControl>
            );
          }}
          name={'streetNumber'}
        />
        <Controller
          control={control}
          name="postalCode"
          render={({ field: { value, onChange } }) => (
            <FormControl isInvalid={!!errors.postalCode}>
              <FormLabel htmlFor="postalCode">{translations('quick_contacts_add_new_contact_postal_code')}</FormLabel>
              <Input
                value={value || ''}
                onChange={onChange}
                variant="transparentUnderline"
                my="auto"
                id={'postalCode'}
                onBlur={trimValue('postalCode')}
              />
              <FormErrorMessage>{errors.postalCode?.message?.toString()}</FormErrorMessage>
            </FormControl>
          )}
        />
      </Flex>
      <Flex w={'100%'} gap={4}>
        <Controller
          control={control}
          name={'country'}
          render={({ field: { value, name, onChange } }) => (
            <FormControl isInvalid={!!errors.country}>
              <FormLabel htmlFor={'country'}>{translations('quick_contacts_add_new_contact_country')}</FormLabel>
              <CountrySelect name={name} selectedValue={value} onSelect={handleCountryChange(onChange)} />
              <FormErrorMessage>{errors.country?.message?.toString()}</FormErrorMessage>
            </FormControl>
          )}
        ></Controller>
        <Controller
          control={control}
          name="city"
          render={({ field: { name, value, onChange } }) => (
            <FormControl isInvalid={!!errors.city}>
              <FormLabel htmlFor={'city'}>
                {translations('quick_contacts_add_new_contact_city')}
                <TooltipHelp
                  label={translations('quick_contacts_address_city_tooltip')}
                  bgColor={'grey.600'}
                  color={'white'}
                />
              </FormLabel>
              <CitySelect
                name={name}
                selectedValue={value}
                onSelect={onChange}
                country={selectedCountry}
                isDisabled={isCityDisabled}
              />
              <FormErrorMessage>{errors.city?.message?.toString()}</FormErrorMessage>
            </FormControl>
          )}
        />
      </Flex>
    </>
  );
};
